"use client"

import { WifiOff, RefreshCw, Loader2 } from 'lucide-react'
import { useStore } from '@/lib/store'
import { cn } from '@/lib/utils'
import { useT } from '@/lib/i18n'

/**
 * Slim connection-status strip under the header. Three states:
 *  - offline               → red: sales are being saved locally
 *  - online + queued sales → indigo: tap to sync now (auto-sync also runs)
 *  - online + empty queue  → renders nothing
 */
export function OfflineBanner() {
  const t = useT()
  const isOnline = useStore((s) => s.isOnline)
  const pending = useStore((s) => s.offlinePending)
  const syncing = useStore((s) => s.isSyncingOffline)
  const syncOfflineSales = useStore((s) => s.syncOfflineSales)

  if (isOnline && pending === 0) return null

  return (
    <div
      className={cn(
        'shrink-0 rounded-2xl border px-4 py-2 flex items-center gap-2.5 text-xs font-bold',
        !isOnline
          ? 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/25 text-red-700 dark:text-red-400'
          : 'bg-indigo-50 dark:bg-indigo-500/10 border-indigo-200 dark:border-indigo-500/25 text-indigo-700 dark:text-indigo-400'
      )}
    >
      {!isOnline ? (
        <>
          <WifiOff className="size-4 shrink-0" />
          <span className="flex-1 min-w-0 truncate">
            {t('offline.off')}
            {pending > 0 && ` ${t('offline.waiting', { n: pending })}`}
          </span>
        </>
      ) : (
        <>
          {syncing
            ? <Loader2 className="size-4 shrink-0 animate-spin" />
            : <RefreshCw className="size-4 shrink-0" />}
          <span className="flex-1 min-w-0 truncate">
            {syncing ? t('offline.syncing') : t('offline.pending', { n: pending })}
          </span>
          {!syncing && (
            <button
              onClick={() => syncOfflineSales()}
              className="press shrink-0 h-7 px-3 rounded-lg bg-indigo-600 text-white text-[12px] font-black hover:bg-indigo-700 transition-colors"
            >
              {t('offline.sync')}
            </button>
          )}
        </>
      )}
    </div>
  )
}
